// PR-22: minimal admin page for the self-built error monitor.
// Surfaces rows from /api/admin/errors with a triage workflow:
// list → resolve. No grouping / dedup / search yet — those land
// once dogfood shows what shape the noise actually has.
//
// Embedded as a tab inside AdminShell. Owns its own filters/state;
// the shell handles the tab nav + back-to-app button.

import { useCallback, useEffect, useState } from 'react'
import { api, type ErrorRow } from '../api'

type SourceFilter = 'all' | 'frontend' | 'backend'
// PR-24: level toggle. Defaults to `error` so the WARN stream
// (slow-query logs + GLM "thinking-after-tool_use" quirk metric +
// any other tracing::warn!()) stays out of the triage queue unless
// explicitly toggled in. `all` shows every captured row.
type LevelFilter = 'error' | 'warn' | 'all'

export function AdminErrors() {
  const [rows, setRows] = useState<ErrorRow[] | null>(null)
  const [unresolvedCount, setUnresolvedCount] = useState<number>(0)
  const [includeResolved, setIncludeResolved] = useState(false)
  const [source, setSource] = useState<SourceFilter>('all')
  const [level, setLevel] = useState<LevelFilter>('error')
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setError(null)
    try {
      const res = await api.listAdminErrors({
        includeResolved,
        source: source === 'all' ? undefined : source,
        level: level === 'all' ? undefined : level,
        limit: 100,
      })
      setRows(res.errors)
      setUnresolvedCount(res.unresolved_count)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [includeResolved, source, level])

  useEffect(() => {
    void reload()
  }, [reload])

  const handleResolve = async (id: string) => {
    setBusyId(id)
    try {
      await api.resolveAdminError(id)
      await reload()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div style={{ fontSize: 13, color: 'var(--ink-1)' }}>
      <div
        style={{
          fontSize: 12,
          color: 'var(--ink-3)',
          marginBottom: 12,
        }}
      >
        {unresolvedCount} unresolved
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 14,
          flexWrap: 'wrap',
        }}
      >
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
        >
          <input
            type="checkbox"
            checked={includeResolved}
            onChange={(e) => setIncludeResolved(e.target.checked)}
          />
          show resolved
        </label>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['all', 'frontend', 'backend'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSource(s)}
              style={{
                background: source === s ? 'var(--paper-2)' : 'transparent',
                border: '1px solid var(--rule-s)',
                color: source === s ? 'var(--ink-1)' : 'var(--ink-3)',
                padding: '3px 10px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              {s}
            </button>
          ))}
        </div>
        {/* PR-24: level toggle (Error / Warn / All). Defaults to Error
            so the WARN-level capture stream (slow queries from sqlx,
            the GLM thinking-after-tool_use metric, etc.) doesn't bury
            real ERROR rows. */}
        <div style={{ display: 'flex', gap: 4 }}>
          {(['error', 'warn', 'all'] as const).map((lv) => (
            <button
              key={lv}
              type="button"
              onClick={() => setLevel(lv)}
              style={{
                background: level === lv ? 'var(--paper-2)' : 'transparent',
                border: '1px solid var(--rule-s)',
                color: level === lv ? 'var(--ink-1)' : 'var(--ink-3)',
                padding: '3px 10px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              {lv}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => void reload()}
          style={{
            background: 'transparent',
            border: '1px solid var(--rule-s)',
            color: 'var(--ink-2)',
            padding: '3px 10px',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 12,
            marginLeft: 'auto',
          }}
        >
          refresh
        </button>
      </div>

      {error && (
        <div
          style={{
            background: 'var(--red-soft)',
            border: '1px solid var(--red)',
            padding: '6px 10px',
            borderRadius: 4,
            color: 'var(--red)',
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {rows === null ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>
          {includeResolved ? 'no errors recorded yet' : 'no unresolved errors — nice'}
        </div>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
          }}
        >
          <thead>
            <tr style={{ borderBottom: '1px solid var(--rule-s)', textAlign: 'left' }}>
              <th style={th}>occurred_at</th>
              <th style={th}>source</th>
              <th style={th}>level</th>
              <th style={th}>message</th>
              <th style={th}>location</th>
              <th style={th}>user_id</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <ErrorRowView
                key={r.id}
                row={r}
                busy={busyId === r.id}
                onResolve={() => void handleResolve(r.id)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

const th: React.CSSProperties = {
  padding: '6px 8px',
  fontWeight: 500,
  color: 'var(--ink-3)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
}

const td: React.CSSProperties = {
  padding: '6px 8px',
  verticalAlign: 'top',
  borderBottom: '1px solid var(--rule-xs, #eee)',
}

function ErrorRowView({
  row,
  busy,
  onResolve,
}: {
  row: ErrorRow
  busy: boolean
  onResolve: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const occurred = new Date(row.occurred_at)
  const occurredStr = `${occurred.toLocaleDateString()} ${occurred.toLocaleTimeString()}`
  return (
    <>
      <tr
        style={{
          opacity: row.resolved ? 0.55 : 1,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <td style={td}>{occurredStr}</td>
        <td style={td}>
          <span
            style={{
              background: row.source === 'frontend' ? '#eef' : '#efe',
              padding: '1px 6px',
              borderRadius: 3,
              fontSize: 11,
            }}
          >
            {row.source}
          </span>
        </td>
        <td style={td}>{row.level}</td>
        <td style={{ ...td, maxWidth: 420, wordBreak: 'break-word' }}>
          {row.message}
        </td>
        <td style={{ ...td, maxWidth: 240, wordBreak: 'break-all', color: 'var(--ink-3)' }}>
          {row.location ?? '—'}
        </td>
        <td style={{ ...td, color: 'var(--ink-3)', fontFamily: 'monospace', fontSize: 11 }}>
          {row.user_id ? row.user_id.slice(0, 8) : '—'}
        </td>
        <td style={td} onClick={(e) => e.stopPropagation()}>
          {row.resolved ? (
            <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>resolved</span>
          ) : (
            <button
              type="button"
              onClick={onResolve}
              disabled={busy}
              style={{
                background: 'transparent',
                border: '1px solid var(--rule-s)',
                color: 'var(--ink-2)',
                padding: '2px 8px',
                borderRadius: 3,
                cursor: busy ? 'wait' : 'pointer',
                fontSize: 11,
              }}
            >
              {busy ? '…' : 'resolve'}
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ padding: '8px 12px', background: 'var(--paper-1, #fafafa)' }}>
            {row.stack_trace && (
              <details open style={{ marginBottom: 6 }}>
                <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--ink-3)' }}>
                  stack_trace
                </summary>
                <pre style={preStyle}>{row.stack_trace}</pre>
              </details>
            )}
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--ink-3)' }}>
                context
              </summary>
              <pre style={preStyle}>{JSON.stringify(row.context, null, 2)}</pre>
            </details>
          </td>
        </tr>
      )}
    </>
  )
}

const preStyle: React.CSSProperties = {
  margin: '4px 0 0',
  padding: 8,
  background: 'var(--paper-0, white)',
  border: '1px solid var(--rule-s)',
  borderRadius: 4,
  fontSize: 11,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 240,
  overflow: 'auto',
}
